import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui.js';
import { Icon } from './Icon.js';

/**
 * How much room the sheet takes once the screen is wide enough to have a say in
 * it. Below `sm` every size is the same full-width sheet, so this only ever
 * describes the desktop.
 *
 * `wide` is for a form whose fields pair up. `full` is for a dialog with a
 * document in it: there the reader is looking at a page rather than filling in
 * boxes, and 448px is a column of a page rather than a page. `widest` is for a
 * dialog that is a working surface — the digital-document builder puts a form
 * and its live preview side by side, and at `full` one of the two is a gutter.
 */
const SHEET_WIDTH = {
  default: 'sm:max-w-md',
  wide: 'sm:max-w-2xl',
  full: 'sm:max-w-4xl',
  widest: 'sm:max-w-5xl',
} as const;

export type ModalSize = keyof typeof SHEET_WIDTH;

/**
 * Which dialog Escape belongs to.
 *
 * Dialogs stack: a confirmation opens over a form, a scan opens over the screen
 * that will hold the scan. Every open one listens on `document`, so one Escape
 * used to close the lot — press it to dismiss "are you sure?" and the screen it
 * was asking about went with it.
 *
 * The newest one wins, which is the one the reader is looking at. A module
 * level array rather than a context, because the stack is the document's, not
 * any one tree's: `createPortal` puts these side by side under `<body>`, and
 * two unrelated branches of the app can each own one.
 */
const escapeStack: symbol[] = [];

/**
 * Accessible dialog (UI-006): focus moves in on open and returns on close,
 * Escape dismisses, and Tab is trapped inside while it is open.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  headerActions,
  hideClose = false,
  size = 'default',
  layer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Drops the ✕ from the corner, for a dialog whose contents are themselves a
   * screen with a way off it. Escape and the backdrop still close it, so this
   * removes a control rather than a way out — and it is only ever right where
   * something on the screen below says, more precisely, what leaving does.
   */
  hideClose?: boolean;
  /**
   * Controls that belong to the dialog as a whole rather than to what is in it
   * — an overflow menu, most of the time. They sit beside the close button so
   * that a dialog which is a screen of its own reads like one.
   */
  headerActions?: ReactNode;
  size?: ModalSize;
  layer?: number;
}) {
  const { t } = useTranslation();

  return (
    <ModalShell open={open} label={title} onClose={onClose} size={size} layer={layer}>
      <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border shrink-0">
        <h2 className="text-base font-semibold truncate">{title}</h2>
        <div className="flex items-center gap-1 shrink-0">
          {headerActions}
          {!hideClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              /* A thumb needs 44px; a cursor does not, and a 44px box beside a
                 16px title makes the header look like it has a hole in it. The
                 minimum applies where the sheet is dragged at with a finger. */
              className="p-1.5 rounded-lg hover:bg-muted transition-colors shrink-0 flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
            >
              <Icon name="close" size={18} />
            </button>
          )}
        </div>
      </header>
      {/* The sheet sits on the bottom edge of the screen, which on a phone
          with a home indicator is not the bottom of the safe area — without
          this the last button in a dialog is half under the gesture bar. */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4">
        {children}
      </div>
      {footer && (
        <footer className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3">
          {footer}
        </footer>
      )}
    </ModalShell>
  );
}

/**
 * A dialog with nothing in it — the portal, the backdrop, the sheet, and
 * everything UI-006 asks of a dialog: focus in on open and back out on close,
 * Escape, Tab trapped inside, the page behind held still.
 *
 * `Modal` above is this plus a header, a scrolling body and a footer, which is
 * what nearly every dialog in the app wants. The two create screens (QUO-1.4,
 * SIG-3.12) want the shell alone: they are screens with a header and a summary
 * bar of their own, so poured into `Modal` they would grow a second header
 * above their first. Sharing the shell rather than copying it means there is
 * one dialog in this product, and it behaves the same wherever it opens.
 */
export function ModalShell({
  open,
  label,
  onClose,
  children,
  size = 'default',
  layer,
}: {
  open: boolean;
  /** Named for a screen reader; the visible title is the caller's own header. */
  label: string;
  onClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  layer?: number;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogBehaviour(open, dialogRef, onClose);

  if (!open) return null;

  return createPortal(
    <div
      style={layer === undefined ? undefined : {zIndex:layer}}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-foreground/25 animate-in fade-in duration-150"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {/*
        The sheet stops above the bottom bar rather than covering it. A dialog
        that hides the navigation strands the reader inside it — the only way
        on is the dialog's own close button, and on a phone the bar is where
        every other way out of a screen lives. `md` and not `sm`, because that
        is the width at which `BottomNav` itself disappears.
      */}
      <div
        className={`bg-card w-full ${SHEET_WIDTH[size]} max-h-[calc(92vh-4rem)] md:max-h-[92vh] mb-16 md:mb-0 flex flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-xl animate-fade-up`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        ref={dialogRef}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Everything UI-006 asks of a dialog that is not markup: focus in on open and
 * back out on close, Escape (for the dialog on top only), Tab trapped inside,
 * the page behind held still.
 *
 * Out of `ModalShell` so that a dialog which is not a sheet can still be the
 * same dialog. The live voice screen (VOICE-001) is a full-screen surface on a
 * phone and a panel of its own design on a desktop, and neither fits the
 * sheet's frame — but it must not get a second, slightly different Escape
 * stack or focus trap, or stacked dialogs would start disagreeing about which
 * of them a key belongs to.
 *
 * Focus goes to the element marked `data-autofocus` when there is one, else to
 * the first focusable element. A sheet's first control is its ✕, which is a
 * fine place to land; the voice screen's first control is "cancel", and Enter
 * on arrival should not throw a recording away.
 */
export function useDialogBehaviour(
  open: boolean,
  dialogRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  /**
   * Callers pass `onClose` as an inline arrow, so its identity changes on every
   * render of the component that owns the dialog. With `onClose` in the
   * dependency list below, typing a single character into a field inside the
   * dialog re-rendered the owner, tore the effect down — which restores focus to
   * whatever opened the dialog — and re-ran it, which focuses the first
   * focusable element: the close button in the header.
   *
   * The result was that the first letter landed in the field and every letter
   * after it went nowhere, and the first space pressed the close button and
   * dismissed the dialog. Confirmed in the browser: one keystroke into the
   * "save as template" name field left `document.activeElement` on the ✕.
   *
   * Reading the latest callback through a ref keeps Escape wired to the current
   * one while the effect itself runs only when the dialog actually opens or
   * closes.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const mine = Symbol('dialog');
    escapeStack.push(mine);

    const focusable = () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => !el.hasAttribute('disabled'));

    (dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]:not([disabled])') ?? focusable()[0])?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Only the dialog on top answers. Tab needs no such rule: the elements
        // it walks between are this dialog's own, and focus standing inside a
        // dialog above is in none of them, so the check below simply misses.
        if (escapeStack[escapeStack.length - 1] !== mine) return;
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      escapeStack.splice(escapeStack.indexOf(mine), 1);
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus();
    };
  }, [open, dialogRef]);
}

/** Confirmation gate for destructive actions (delete flows across all modules). */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  loading = false,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={loading}>
            {confirmLabel ?? t('common.delete')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">{message}</p>
    </Modal>
  );
}
